import { create } from "zustand";
import { persist } from "zustand/middleware";
import { queryClient } from "@/app/providers/queryClient";
import type { User } from "@fysaro/domain";
import i18n from "i18next";
import { applyBranding } from "@/design-system/lib/apply-branding";
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from "@/shared/lib/supabase";

interface AuthState {
  token: string | null;
  user: User | null;
  activeClinicId: string | null;
  settings: User["settings"] | null;
  subscription: User["subscription"] | null;
  tenantBilling: User["tenantBilling"] | null;
  isInitializing: boolean;

  setInitializing: (val: boolean) => void;
  setToken: (token: string | null) => void;
  resetSessionContext: () => void;
  setUser: (user: User | null) => void;
  setActiveClinicId: (id: string | null) => void;

  logout: () => void;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      activeClinicId: null,
      settings: null,
      subscription: null,
      tenantBilling: null,
      isInitializing: true,
      _hasHydrated: false,

      setInitializing: (val) => set({ isInitializing: val }),
      setToken: (token) => set({ token }),

      resetSessionContext: () => {
        queryClient.clear();
        set({
          token: null,
          user: null,
          activeClinicId: null,
          settings: null,
          subscription: null,
          tenantBilling: null,
        });
        localStorage.removeItem("clinic-branding");
        sessionStorage.removeItem("booking-flow-storage");
      },

      setUser: (user) => {
        const currentUser = get().user;
        const currentActiveId = get().activeClinicId;
        const isSameUser =
          Boolean(currentUser?.id) && currentUser?.id === user?.id;
        const accessibleClinicIds = user?.clinics?.map((c) => c.id) ?? [];
        const serverClinicId =
          user?.activeClinicId || user?.lastActiveClinicId || null;
        const localClinicId = isSameUser ? currentActiveId : null;

        const pickAccessibleClinic = (clinicId: string | null) =>
          clinicId && accessibleClinicIds.includes(clinicId) ? clinicId : null;

        const resolvedActiveClinicId = (() => {
          if (isSameUser && localClinicId) {
            if (accessibleClinicIds.includes(localClinicId)) {
              return localClinicId;
            }

            // /me can lag right after booking at a new clinic; keep the local pin.
            if (
              currentUser?.activeClinicId === localClinicId ||
              currentUser?.lastActiveClinicId === localClinicId
            ) {
              return localClinicId;
            }
          }

          return (
            pickAccessibleClinic(serverClinicId) ||
            pickAccessibleClinic(localClinicId) ||
            accessibleClinicIds[0] ||
            null
          );
        })();

        const shouldAugmentClinics =
          isSameUser &&
          resolvedActiveClinicId &&
          !accessibleClinicIds.includes(resolvedActiveClinicId);
        const placeholderClinic = currentUser?.clinics.find(
          (clinic) => clinic.id === resolvedActiveClinicId,
        );

        set({
          user: shouldAugmentClinics
            ? {
                ...user,
                clinics: [
                  ...user.clinics,
                  placeholderClinic ?? {
                    id: resolvedActiveClinicId,
                    name: "",
                    city: null,
                    logoUrl: null,
                    isMain: false,
                    branding: null,
                  },
                ],
                activeClinicId: resolvedActiveClinicId,
                lastActiveClinicId: resolvedActiveClinicId,
              }
            : user,
          activeClinicId: resolvedActiveClinicId,
          settings: user?.settings || null,
          subscription: user?.subscription || null,
          tenantBilling: user?.tenantBilling || null,
        });

        if (!user) return;

        // ðŸŒ Sincronizar Idioma
        const userLang = user.language;
        if (userLang && i18n.language !== userLang) {
          i18n.changeLanguage(userLang);
        }

        // 🎨 Sincronizar Tema
        const userTheme = user.theme || "light";
        document.documentElement.setAttribute("data-theme", userTheme);
        document.documentElement.classList.toggle("dark", userTheme === "dark");
        localStorage.setItem("theme", userTheme);

        // ðŸ¢ Aplicar Branding inicial (Buscamos en sus settings globales o clí­nica asociada)
        if (user.settings?.branding) {
          applyBranding(user.settings.branding);
          localStorage.setItem(
            "clinic-branding",
            JSON.stringify(user.settings.branding),
          );
        }
      },

      setActiveClinicId: (id) => {
        const currentUser = get().user;
        const currentActiveId = get().activeClinicId;

        if (currentActiveId === id) return;

        queryClient.clear();

        // ðŸ›¡ï¸ Si no hay usuario, actualizamos solo la raí­z por seguridad
        if (!currentUser) {
          set({ activeClinicId: id });
          return;
        }

        // ðŸŽ¯ Sincronizamos quirúrgicamente en cascada para que NADA lea datos viejos
        set({
          activeClinicId: id,
          user: {
            ...currentUser,
            activeClinicId: id ?? null,
            lastActiveClinicId: id ?? null,
          },
        });
      },

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      logout: () => {
        // Supabase global sign-out can fail before it clears browser storage.
        // Remove the local session first so no account can be resurrected on
        // the next app load; the local Supabase sign-out then emits SIGNED_OUT.
        localStorage.removeItem(SUPABASE_AUTH_STORAGE_KEY);
        localStorage.removeItem(`${SUPABASE_AUTH_STORAGE_KEY}-code-verifier`);
        void supabase.auth.signOut({ scope: "local" });

        // 1. Limpiamos React Query al instante
        queryClient.clear();

        // 2. Vaciamos el estado de Zustand (al poner token en null, el AppGate se activará solo)
        set({
          user: null,
          token: null,
          activeClinicId: null,
          isInitializing: false,
        });

        // 3. Limpiamos el disco por seguridad
        localStorage.removeItem("auth-storage");
        localStorage.removeItem("clinic-branding");
        sessionStorage.removeItem("booking-flow-storage");
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        activeClinicId: state.activeClinicId,
        settings: state.settings,
        subscription: state.subscription,
        tenantBilling: state.tenantBilling,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);

        // Al levantar la app, respetamos el tema guardado
        const savedTheme = localStorage.getItem("theme") || "light";
        document.documentElement.setAttribute("data-theme", savedTheme);
        document.documentElement.classList.toggle(
          "dark",
          savedTheme === "dark",
        );

        const savedBranding = localStorage.getItem("clinic-branding");
        if (savedBranding) {
          try {
            applyBranding(JSON.parse(savedBranding));
          } catch (e) {
            console.error("Error rehydrating branding", e);
          }
        }
      },
    },
  ),
);
